<script setup>
import { Head } from '@inertiajs/vue3';
import AppLayout from '@/Layouts/AppLayout.vue';
import VariantPageWrapper from '@/Components/VariantPageWrapper.vue';
import { isBrandDirect as checkBrandDirect } from '@/lib/brand';
import LeadFunnel from '@/Components/LeadFunnel.vue';
import { ShieldCheckIcon, ClockIcon, MapPinIcon } from '@heroicons/vue/24/outline';
import { useDesignSet } from '@/composables/useDesignSet';

const props = defineProps({
    domain: Object,
    service: String,
    branding: Object,
    designVariant: Object,
    spotImages: Object,
    // AGENT.md anti-detection rule #7: per-domain author persona,
    // hydrated by StaticPageController via AuthorResolver.
    missionAuthor: { type: Object, default: null },
});

const { isClassic } = useDesignSet();

const serviceLabel = props.service === 'wp' ? 'Wärmepumpen' : 'Photovoltaik';
const isBrandDirect = checkBrandDirect(props.domain);
const brandName = props.domain?.settings?.brand_name || props.domain?.name || 'Portal';
</script>

<template>
    <Head :title="`Über uns - Vergleichsportal für ${serviceLabel}`">
        <meta name="description" :content="`${brandName}: Vergleichsportal für ${serviceLabel}. Kostenlos und unverbindlich Angebote regionaler Fachbetriebe einholen.`" />
        <meta property="og:title" :content="isBrandDirect ? `Über ${brandName}` : 'Über uns - Photovoltaik-Vergleichsportal für ganz Deutschland'" />
        <meta property="og:type" content="website" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
        <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,600;9..144,700&family=Inter:wght@400;500;600;700&display=swap" />
    </Head>

    <AppLayout :domain="domain" :branding="branding" :designVariant="designVariant" :service="service" activePage="about" :heroMode="!isClassic" :hideChrome="!!domain?.settings?.design_variant">
        <VariantPageWrapper :domain="domain">
        <div class="smi-ab">
        <!-- Hero -->
        <section
            class="ds-hero"
            :class="{ 'ds-hero--with-image': !isClassic && spotImages?.about_mission }"
            :style="!isClassic && spotImages?.about_mission ? { backgroundImage: `url(${spotImages.about_mission})` } : {}"
            aria-label="Über uns Einführung"
        >
            <div v-if="!isClassic" class="ds-hero__overlay" aria-hidden="true"></div>
            <div class="ds-hero__inner">
                <div class="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <span v-if="!isClassic" class="ds-hero__badge">Über uns</span>
                    <h1 v-if="isBrandDirect" class="ds-hero__title">Über <span class="ds-hero__accent">{{ brandName }}.</span></h1>
                    <h1 v-else class="ds-hero__title">Wir machen Deutschland <span class="ds-hero__accent">grüner.</span></h1>
                    <p class="ds-hero__subtitle">{{ brandName }} ist ein Vergleichsportal für {{ serviceLabel }}. Wir vermitteln Ihre Anfrage an regionale Fachbetriebe - kostenlos und unverbindlich für Sie.</p>
                </div>
            </div>
        </section>

        <!-- Mission -->
        <section class="ds-section ds-section--gradient" aria-label="Unsere Mission">
            <div class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                <div class="ds-mission">
                    <div
                        class="ds-mission__image-panel"
                        role="img"
                        aria-label="Bild unserer Mission"
                        :style="spotImages?.about_mission ? { backgroundImage: `url(${spotImages.about_mission})` } : { background: 'linear-gradient(135deg, #0f172a 0%, #1E293B 100%)' }"
                    >
                        <div class="ds-mission__image-overlay" aria-hidden="true"></div>
                    </div>
                    <div class="ds-mission__content">
                        <span class="ds-mission__eyebrow">Unsere Mission</span>
                        <h2 v-if="isBrandDirect" class="ds-mission__title">Unser Anspruch.</h2>
                        <h2 v-else class="ds-mission__title">Warum wir tun, was wir tun.</h2>
                        <template v-if="isBrandDirect">
                            <p class="ds-mission__text">{{ brandName }} ist ein Vergleichsportal. Wir vermitteln Ihre Anfrage an regionale Fachbetriebe - die Planung und Installation übernimmt der Betrieb, den Sie selbst wählen.</p>
                            <p class="ds-mission__text">Unser Ziel ist es, Hausbesitzern den Umstieg auf erneuerbare Energien so einfach und transparent wie möglich zu machen.</p>
                        </template>
                        <template v-else>
                            <p class="ds-mission__text">Deutschland hat ein gewaltiges Potenzial für erneuerbare Energien. Unser Ziel ist es, Hausbesitzern den Umstieg auf Photovoltaik und moderne Heiztechnik so einfach wie möglich zu machen.</p>
                            <p class="ds-mission__text">Dabei setzen wir auf ein bundesweites Netzwerk aus regionalen Fachbetrieben, und regionale Fachbetriebe zusammenzubringen.</p>
                        </template>
                        <div class="ds-mission__trust-row">
                            <div class="ds-mission__trust-item">
                                <ShieldCheckIcon class="ds-mission__trust-icon" aria-hidden="true" />
                                <span>{{ isBrandDirect ? 'Persönlicher Service' : 'Kostenlos für Sie' }}</span>
                            </div>
                            <div class="ds-mission__trust-item">
                                <ClockIcon class="ds-mission__trust-icon" aria-hidden="true" />
                                <span>Seit 2024 aktiv</span>
                            </div>
                            <div class="ds-mission__trust-item">
                                <MapPinIcon class="ds-mission__trust-icon" aria-hidden="true" />
                                <span>Bundesweit</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Stats -->
        <section class="ds-section ds-section--alt" aria-label="Statistiken">
            <div class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                <h2 class="ds-stats__heading">So ist das Portal aufgebaut</h2>
                <div class="ds-stats-grid" role="list">
                    <div
                        v-for="stat in [
                            { value: 'Kostenlos', label: 'für Hauseigentümer' },
                            { value: 'Bundesweit', label: 'regionale Fachbetriebe' },
                            { value: 'Bis zu 3', label: 'Angebote je Anfrage' },
                            { value: 'Unverbindlich', label: 'ohne Vertragszwang' }
                        ]"
                        :key="stat.label"
                        class="ds-stat-card"
                        role="listitem"
                    >
                        <div class="ds-stat-card__value">{{ stat.value }}</div>
                        <div class="ds-stat-card__divider"></div>
                        <div class="ds-stat-card__label">{{ stat.label }}</div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Values / What sets us apart -->
        <section class="ds-section ds-section--green" aria-label="Unsere Werte">
            <div class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                <h2 class="ds-values__heading">Was uns auszeichnet</h2>
                <div class="ds-values-grid" role="list">
                    <div
                        v-for="(item, idx) in [
                            { title: 'Kostenlos', desc: 'Die Anfrage ist für Sie kostenlos und unverbindlich. Wir finanzieren uns über eine Vermittlungsvergütung der Fachbetriebe.' },
                            { title: 'Transparent', desc: 'Keine versteckten Kosten, keine Tricks. Sie erhalten echte Vergleichsangebote von qualifizierten Fachbetrieben.' },
                            { title: 'Regional', desc: 'Wir kennen die lokalen Fördermittel und regionalen Anforderungen und verbinden Sie mit Betrieben vor Ort.' },
                        ]"
                        :key="item.title"
                        class="ds-value-card"
                        role="listitem"
                    >
                        <div class="ds-value-card__number">{{ idx + 1 }}</div>
                        <h3 class="ds-value-card__title">{{ item.title }}</h3>
                        <p class="ds-value-card__desc">{{ item.desc }}</p>
                    </div>
                </div>
            </div>
        </section>

        <!-- Regional Focus -->
        <section class="ds-section ds-section--alt" aria-label="Bundesweite Abdeckung">
            <div class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                <div class="ds-regional">
                    <div class="ds-regional__content">
                        <h2 class="ds-regional__title">Bundesweit für Sie da.</h2>
                        <p class="ds-regional__text">Ob Hamburg, Berlin, München oder Köln - unser Netzwerk umfasst regionale Fachbetriebe in zahlreichen Städten. Wir kennen die lokalen Fördermittel und regionalen Anforderungen.</p>
                        <div class="ds-regional__callout">
                            <p class="ds-regional__callout-label">Wussten Sie schon?</p>
                            <p class="ds-regional__callout-text">Ob Photovoltaik auf dem Dach oder Wärmepumpe im Bestand: Mit guter Planung und dem richtigen Fachbetrieb lassen sich überall in Deutschland wirtschaftliche und langlebige Energielösungen umsetzen.</p>
                        </div>
                    </div>
                    <div
                        class="ds-regional__image-panel"
                        role="img"
                        aria-label="Bundesweites Netzwerk"
                        :style="spotImages?.about_regional ? { backgroundImage: `url(${spotImages.about_regional})` } : { background: 'linear-gradient(135deg, #1E293B 0%, #ea580c 100%)' }"
                    >
                        <div class="ds-regional__image-overlay" aria-hidden="true"></div>
                    </div>
                </div>
            </div>
        </section>

        <!-- AGENT.md rule #7: per-domain author persona. CSS-namespaced
             via aut-* so we never collide with pilot variant prefixes
             (v01_*, profi-*, rittinger-*, etc.). -->
        <section v-if="missionAuthor" class="aut-section" aria-label="Über die redaktionelle Verantwortung">
            <div class="aut-wrap">
                <div class="aut-eyebrow">Redaktionelle Verantwortung</div>
                <h2 class="aut-title">
                    Hinter dieser Seite steht <span class="aut-title-accent">{{ missionAuthor.name }}</span>.
                </h2>
                <p v-if="missionAuthor.headline" class="aut-headline">{{ missionAuthor.headline }}</p>
                <div class="aut-grid">
                    <div class="aut-photo" aria-hidden="true">
                        <div class="aut-photo-fallback">{{ (missionAuthor.vorname || missionAuthor.name).charAt(0) }}{{ (missionAuthor.nachname || '').charAt(0) }}</div>
                    </div>
                    <div class="aut-body">
                        <p v-for="(para, i) in (missionAuthor.bio_long || '').split('\n\n')" :key="i" class="aut-para">{{ para }}</p>
                    </div>
                </div>
                <div class="aut-meta">
                    <div v-if="missionAuthor.credentials && missionAuthor.credentials.length" class="aut-creds">
                        <div class="aut-creds-label">Qualifikationen</div>
                        <ul class="aut-creds-list">
                            <li v-for="(c, i) in missionAuthor.credentials" :key="i">{{ c }}</li>
                        </ul>
                    </div>
                    <div class="aut-facts">
                        <div v-if="missionAuthor.years_experience" class="aut-fact">
                            <span class="aut-fact-num">{{ missionAuthor.years_experience }}</span>
                            <span class="aut-fact-label">Jahre Berufserfahrung</span>
                        </div>
                        <div v-if="missionAuthor.region" class="aut-fact">
                            <span class="aut-fact-num">●</span>
                            <span class="aut-fact-label">{{ missionAuthor.region }}</span>
                        </div>
                    </div>
                    <div v-if="missionAuthor.linkedin_url || missionAuthor.xing_url || missionAuthor.email" class="aut-social">
                        <a v-if="missionAuthor.linkedin_url" :href="missionAuthor.linkedin_url" target="_blank" rel="noopener nofollow" class="aut-social-link">LinkedIn</a>
                        <a v-if="missionAuthor.xing_url" :href="missionAuthor.xing_url" target="_blank" rel="noopener nofollow" class="aut-social-link">XING</a>
                        <a v-if="missionAuthor.email" :href="`mailto:${missionAuthor.email}`" class="aut-social-link">E-Mail</a>
                    </div>
                </div>
            </div>
        </section>

        <!-- CTA -->
        <section class="ds-cta" aria-label="Kontakt aufnehmen">
            <div class="ds-cta__inner">
                <div class="max-w-5xl mx-auto px-6 lg:px-10 text-center">
                    <h2 class="ds-cta__title">Haben Sie Fragen?</h2>
                    <p class="ds-cta__subtitle">Kontaktieren Sie uns - wir beraten Sie gerne kostenlos und unverbindlich.</p>
                    <div class="max-w-2xl mx-auto mt-10">
                        <div class="p-6 md:p-10" :style="{ background: 'var(--dv-card, #FFFFFF)', border: '1px solid var(--dv-border, #E8E4DB)', borderRadius: '6px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }">
                            <LeadFunnel :domain="domain" :service="service" :design-variant="designVariant" bare :dark-mode="false" />
                        </div>
                    </div>
                </div>
            </div>
        </section>
        </div>
        </VariantPageWrapper>
    </AppLayout>
</template>

<style scoped>
/* ===================================================================
   SECTION FADE-IN ANIMATION
   =================================================================== */
.section-fade-in {
    opacity: 0;
    transform: translateY(20px);
    animation: sectionReveal 0.5s ease-out forwards;
    will-change: transform, opacity;
}

@keyframes sectionReveal {
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

/* ===== HERO ===== */
.ds-hero {
    position: relative;
    min-height: var(--ds-hero-min-height, 280px);
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: var(--dv-section, #f9f9f9);
    overflow: hidden;
    transition: min-height 0.3s ease;
}

.ds-hero--with-image {
    background-size: cover;
    background-position: center 40%;
    background-color: var(--dv-dark, #1a1a2e);
}

.ds-hero__overlay {
    position: absolute;
    inset: 0;
    background: var(--ds-hero-overlay-style, linear-gradient(180deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.7) 100%));
    z-index: 1;
}

.ds-hero__glow {
    position: absolute;
    inset: 0;
    background:
        radial-gradient(ellipse at 20% 50%, var(--dv-glow-a, rgba(137,111,218,0.25)) 0%, transparent 60%),
        radial-gradient(ellipse at 80% 30%, var(--dv-glow-b, rgba(251,144,125,0.2)) 0%, transparent 60%);
    opacity: var(--ds-hero-glow-intensity, 0.25);
    z-index: 2;
    mix-blend-mode: screen;
}

.ds-hero__inner {
    position: relative;
    z-index: 3;
    padding: 100px 0 60px;
    width: 100%;
}

.ds-hero:not(.ds-hero--with-image) .ds-hero__inner {
    padding: 64px 0 48px;
}

.ds-hero__badge {
    display: inline-block;
    padding: 6px 20px;
    border-radius: 24px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    color: #fff;
    background: rgba(255, 255, 255, 0.12);
    backdrop-filter: blur(12px);
    border: 1px solid rgba(255, 255, 255, 0.15);
    margin-bottom: 24px;
}

.ds-hero__title {
    font-family: 'Lexend', sans-serif;
    font-size: var(--ds-hero-title-size, clamp(28px, 4vw, 48px));
    font-weight: var(--ds-hero-title-weight, 700);
    line-height: 1.2;
    margin-bottom: 16px;
    text-shadow: var(--ds-hero-text-shadow, none);
    color: var(--dv-text, #333);
}

.ds-hero--with-image .ds-hero__title { color: #fff; }

.ds-hero__accent {
    color: var(--brand-accent-fg, var(--brand-accent));
}

.ds-hero__subtitle {
    font-family: 'Source Sans 3', sans-serif;
    font-size: 17px;
    line-height: 1.7;
    max-width: 560px;
    margin: 0 auto;
    color: var(--dv-muted, #666);
}

.ds-hero--with-image .ds-hero__subtitle { color: rgba(255, 255, 255, 0.8); }

/* ===== SECTIONS ===== */
.ds-section {
    padding: 64px 0;
    background: var(--dv-page, #fff);
    position: relative;
}

@media (min-width: 768px) {
    .ds-section { padding: 80px 0; }
}

.ds-section--alt {
    background: var(--dv-section, #f9f9f9);
}

/* ===== MISSION ===== */
.ds-mission {
    display: flex;
    flex-direction: column;
    gap: 32px;
    align-items: stretch;
}

@media (min-width: 1024px) {
    .ds-mission {
        flex-direction: row;
        gap: 0;
        min-height: 380px;
    }
}

.ds-mission__image-panel {
    position: relative;
    width: 100%;
    min-height: 260px;
    background-size: cover;
    background-position: center;
    border-radius: var(--dv-radius-xl, 16px);
    overflow: hidden;
}

@media (min-width: 1024px) {
    .ds-mission__image-panel {
        width: 50%;
        min-height: auto;
        border-radius: var(--dv-radius-xl, 16px) 0 0 var(--dv-radius-xl, 16px);
    }
}

.ds-mission__image-overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.15) 100%);
}

.ds-mission__content {
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 0;
}

@media (min-width: 1024px) {
    .ds-mission__content {
        width: 50%;
        padding: 32px 0 32px 48px;
    }
}

/* Mission eyebrow label */
.ds-mission__eyebrow {
    display: inline-block;
    font-family: 'Source Sans 3', sans-serif;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 1.4px;
    text-transform: uppercase;
    color: var(--brand-accent-fg, var(--brand-accent));
    margin-bottom: 10px;
}

.ds-mission__title {
    font-family: 'Lexend', sans-serif;
    font-size: var(--ds-section-title-size, 32px);
    font-weight: 700;
    color: var(--dv-text, #333);
    margin-bottom: 20px;
    line-height: 1.25;
    padding-bottom: 16px;
    border-bottom: 2px solid color-mix(in srgb, var(--brand-accent) 20%, transparent);
}

.ds-mission__text {
    font-family: 'Source Sans 3', sans-serif;
    font-size: 15px;
    color: var(--dv-muted, #666);
    line-height: 1.7;
    margin-bottom: 16px;
}

.ds-mission__text:last-of-type {
    margin-bottom: 0;
}

/* Trust row inside mission */
.ds-mission__trust-row {
    display: flex;
    flex-wrap: wrap;
    gap: 12px 20px;
    margin-top: 24px;
    padding-top: 20px;
    border-top: 1px solid var(--dv-border, #eee);
}

.ds-mission__trust-item {
    display: flex;
    align-items: center;
    gap: 6px;
    font-family: 'Source Sans 3', sans-serif;
    font-size: 13px;
    font-weight: 600;
    color: var(--dv-text, #555);
}

.ds-mission__trust-icon {
    width: 16px;
    height: 16px;
    color: var(--brand-accent-fg, var(--brand-accent));
    flex-shrink: 0;
}

/* ===== STATS ===== */
.ds-stats__heading {
    font-family: 'Lexend', sans-serif;
    text-align: center;
    font-size: var(--ds-section-title-size, clamp(24px, 3vw, 32px));
    font-weight: 700;
    color: var(--dv-text, #333);
    margin-bottom: 40px;
    line-height: 1.25;
}

@media (min-width: 768px) {
    .ds-stats__heading { margin-bottom: 52px; }
}

.ds-stats-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 24px;
}

@media (min-width: 1024px) {
    .ds-stats-grid { grid-template-columns: repeat(4, 1fr); }
}

.ds-stat-card {
    text-align: center;
    padding: 32px 20px;
    background: var(--dv-card, #fff);
    border: none;
    border-top: 3px solid var(--brand-accent);
    border-radius: var(--dv-radius-lg, 12px);
    box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04);
    transform: translateZ(0);
    transition:
        transform 200ms ease-out,
        box-shadow 200ms ease-out;
}

.ds-stat-card:hover {
    box-shadow: 0 2px 6px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.06);
}

.ds-stat-card:focus-visible {
    outline: 3px solid var(--brand-accent);
    outline-offset: 2px;
}

.ds-stat-card__value {
    font-family: 'Lexend', sans-serif;
    font-size: 40px;
    font-weight: 800;
    color: var(--brand-accent-fg, var(--brand-accent));
    margin-bottom: 8px;
    line-height: 1;
    white-space: nowrap;
}

.ds-stat-card__divider {
    width: 32px;
    height: 3px;
    background: var(--brand-accent);
    border-radius: 2px;
    margin: 0 auto 12px;
    opacity: 0.4;
}

.ds-stat-card__label {
    font-family: 'Source Sans 3', sans-serif;
    font-size: 14px;
    color: var(--dv-muted, #666);
    font-weight: 500;
    line-height: 1.4;
}

/* ===== VALUES ===== */
.ds-values__heading {
    font-family: 'Lexend', sans-serif;
    text-align: center;
    font-size: var(--ds-section-title-size, clamp(24px, 3vw, 32px));
    font-weight: 700;
    color: var(--dv-text, #333);
    margin-bottom: 40px;
    line-height: 1.25;
}

@media (min-width: 768px) {
    .ds-values__heading { margin-bottom: 52px; }
}

.ds-values-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 24px;
}

@media (min-width: 768px) {
    .ds-values-grid { grid-template-columns: repeat(3, 1fr); }
}

.ds-value-card {
    padding: 32px 28px;
    background: var(--dv-card, #fff);
    border: none;
    border-top: 3px solid transparent;
    border-radius: var(--dv-radius-lg, 12px);
    box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04);
    transition:
        transform 200ms ease-out,
        box-shadow 200ms ease-out,
        border-top-color 200ms ease-out;
    text-align: center;
}

.ds-value-card:hover {
    box-shadow: 0 2px 6px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.06);
    border-top-color: var(--brand-accent-fg, var(--brand-accent));
}

.ds-value-card:focus-visible {
    outline: 3px solid var(--brand-accent);
    outline-offset: 2px;
}

.ds-value-card__number {
    width: 48px;
    height: 48px;
    margin: 0 auto 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: var(--brand-accent);
    color: #fff;
    font-family: 'Lexend', sans-serif;
    font-size: 18px;
    font-weight: 800;
    box-shadow: 0 4px 12px color-mix(in srgb, var(--brand-accent) 30%, transparent);
}

.ds-value-card__title {
    font-family: 'Lexend', sans-serif;
    font-size: 18px;
    font-weight: 700;
    color: var(--dv-text, #333);
    margin-bottom: 10px;
}

.ds-value-card__desc {
    font-family: 'Source Sans 3', sans-serif;
    font-size: 15px;
    color: var(--dv-muted, #666);
    line-height: 1.65;
}

/* ===== REGIONAL ===== */
.ds-regional {
    display: flex;
    flex-direction: column;
    gap: 32px;
    align-items: stretch;
}

@media (min-width: 1024px) {
    .ds-regional {
        flex-direction: row;
        gap: 0;
        min-height: 380px;
    }
}

.ds-regional__content {
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 0;
}

@media (min-width: 1024px) {
    .ds-regional__content {
        width: 50%;
        padding: 32px 48px 32px 0;
    }
}

.ds-regional__title {
    font-family: 'Lexend', sans-serif;
    font-size: var(--ds-section-title-size, 32px);
    font-weight: 700;
    color: var(--dv-text, #333);
    margin-bottom: 16px;
    line-height: 1.25;
}

.ds-regional__text {
    font-family: 'Source Sans 3', sans-serif;
    font-size: 15px;
    color: var(--dv-muted, #666);
    line-height: 1.7;
    margin-bottom: 24px;
}

.ds-regional__callout {
    padding: 24px;
    background: var(--brand-accent-soft, rgba(251,144,125,0.06));
    border: 1px solid var(--dv-border, rgba(251,144,125,0.15));
    border-radius: var(--dv-radius-lg, 12px);
    border-left: 3px solid var(--brand-accent);
}

.ds-regional__callout-label {
    font-family: 'Lexend', sans-serif;
    font-size: 14px;
    font-weight: 600;
    color: var(--brand-accent-fg, var(--brand-accent));
    margin-bottom: 6px;
}

.ds-regional__callout-text {
    font-family: 'Source Sans 3', sans-serif;
    font-size: 14px;
    color: var(--dv-muted, #666);
    line-height: 1.6;
}

.ds-regional__image-panel {
    position: relative;
    width: 100%;
    min-height: 260px;
    background-size: cover;
    background-position: center;
    border-radius: var(--dv-radius-xl, 16px);
    overflow: hidden;
}

@media (min-width: 1024px) {
    .ds-regional__image-panel {
        width: 50%;
        min-height: auto;
        border-radius: 0 var(--dv-radius-xl, 16px) var(--dv-radius-xl, 16px) 0;
    }
}

.ds-regional__image-overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.15) 100%);
}

/* ===== CTA ===== */
.ds-cta {
    position: relative;
    padding: 80px 0;
    background: var(--dv-section, #f9f9f9);
    overflow: hidden;
}

@media (min-width: 768px) {
    .ds-cta { padding: 100px 0; }
}

.ds-hero--with-image ~ .ds-section ~ .ds-section--alt ~ .ds-section ~ .ds-section--alt ~ .ds-cta,
.ds-hero--with-image ~ * ~ * ~ * ~ * ~ .ds-cta {
    background: var(--dv-dark, #1a1a2e);
}

.ds-cta__glow {
    position: absolute;
    inset: 0;
    background:
        radial-gradient(ellipse at 30% 50%, var(--dv-glow-a, rgba(137,111,218,0.2)) 0%, transparent 60%),
        radial-gradient(ellipse at 70% 50%, var(--dv-glow-b, rgba(251,144,125,0.15)) 0%, transparent 60%);
    opacity: var(--ds-hero-glow-intensity, 0.3);
    z-index: 1;
    mix-blend-mode: screen;
}

.ds-cta__inner {
    position: relative;
    z-index: 2;
}

.ds-cta__title {
    font-family: 'Lexend', sans-serif;
    font-size: var(--ds-section-title-size, 32px);
    font-weight: 700;
    color: var(--dv-text, #333);
    margin-bottom: 12px;
}

.ds-cta__subtitle {
    font-family: 'Source Sans 3', sans-serif;
    font-size: 16px;
    color: var(--dv-muted, #666);
    margin-bottom: 32px;
    line-height: 1.6;
}

/* ====================================================================
   aut-* : per-domain mission-author persona block.
   Namespaced so it never collides with pilot variants (v01_*, etc.)
   or with the global ds-* / aw-* design-system tokens.
   ==================================================================== */
.aut-section {
    background: var(--dv-bg, #FAF8F4);
    padding: 80px 0;
    border-top: 1px solid var(--dv-border, #E8E4DB);
}
.aut-wrap {
    max-width: 960px;
    margin: 0 auto;
    padding: 0 24px;
}
.aut-eyebrow {
    font-family: 'Source Sans 3', sans-serif;
    font-size: 12px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--dv-muted, #6B6B66);
    margin-bottom: 14px;
}
.aut-title {
    font-family: var(--dv-font-heading, 'Lora', Georgia, serif);
    font-size: clamp(26px, 3.4vw, 38px);
    font-weight: 500;
    color: var(--dv-text, #1A1A17);
    letter-spacing: -0.02em;
    line-height: 1.18;
    margin: 0 0 8px 0;
}
.aut-title-accent {
    color: var(--dv-accent, var(--brand-accent, #f97316));
}
.aut-headline {
    font-family: 'Source Sans 3', sans-serif;
    font-size: 17px;
    color: var(--dv-muted, #4a4a45);
    margin: 0 0 36px 0;
}
.aut-grid {
    display: grid;
    grid-template-columns: 140px 1fr;
    gap: 32px;
    align-items: start;
    margin-bottom: 40px;
}
@media (max-width: 640px) {
    .aut-grid { grid-template-columns: 1fr; gap: 20px; }
}
.aut-photo {
    width: 140px;
    height: 140px;
    border-radius: 50%;
    overflow: hidden;
    background: var(--dv-card, #fff);
    border: 1px solid var(--dv-border, #E8E4DB);
}
.aut-photo-fallback {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Source Sans 3', sans-serif;
    font-size: 44px;
    font-weight: 600;
    color: var(--dv-accent, #f97316);
    background: linear-gradient(135deg, rgba(249,115,22,0.06), rgba(249,115,22,0.02));
    letter-spacing: -0.03em;
}
.aut-body { font-family: 'Source Sans 3', sans-serif; }
.aut-para {
    font-size: 16px;
    line-height: 1.72;
    color: var(--dv-text, #2a2a25);
    margin: 0 0 16px 0;
}
.aut-para:last-child { margin-bottom: 0; }
.aut-meta {
    display: grid;
    grid-template-columns: 2fr 1fr;
    gap: 32px;
    padding-top: 32px;
    border-top: 1px solid var(--dv-border, #E8E4DB);
}
@media (max-width: 640px) {
    .aut-meta { grid-template-columns: 1fr; gap: 20px; }
}
.aut-creds-label {
    font-family: 'Source Sans 3', sans-serif;
    font-size: 11px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--dv-muted, #6B6B66);
    margin-bottom: 10px;
}
.aut-creds-list {
    list-style: none;
    padding: 0;
    margin: 0;
    font-family: 'Source Sans 3', sans-serif;
    font-size: 14px;
    color: var(--dv-text, #2a2a25);
}
.aut-creds-list li {
    padding: 4px 0;
    border-bottom: 1px dotted var(--dv-border, #E8E4DB);
}
.aut-creds-list li:last-child { border-bottom: none; }
.aut-facts {
    display: flex;
    flex-direction: column;
    gap: 14px;
}
.aut-fact {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
}
.aut-fact-num {
    font-family: var(--dv-font-heading, 'Lora', Georgia, serif);
    font-size: 28px;
    font-weight: 500;
    color: var(--dv-accent, #f97316);
    line-height: 1;
}
.aut-fact-label {
    font-family: 'Source Sans 3', sans-serif;
    font-size: 12px;
    color: var(--dv-muted, #6B6B66);
    margin-top: 4px;
}
.aut-social {
    grid-column: 1 / -1;
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    padding-top: 8px;
}
.aut-social-link {
    font-family: 'Source Sans 3', sans-serif;
    font-size: 13px;
    text-decoration: none;
    color: var(--dv-accent, #f97316);
    border: 1px solid var(--dv-border, #E8E4DB);
    padding: 7px 14px;
    border-radius: 999px;
    transition: background 120ms ease;
}
.aut-social-link:hover {
    background: rgba(249,115,22,0.05);
}

/* Domain skin: investor / slate + orange, matches Home.vue */
.smi-ab {
    --dv-glow-a: rgba(15, 23, 42, 0.12);
    --dv-glow-b: rgba(234, 88, 12, 0.12);
    --brand-accent: #ea580c;
    --brand-accent-fg: #c2410c;
    background: #f8fafc;
    font-family: Inter, system-ui, sans-serif;
}
.smi-ab .ds-hero__title,
.smi-ab .ds-mission__title,
.smi-ab .ds-stats__heading,
.smi-ab .ds-values__heading,
.smi-ab .ds-regional__title,
.smi-ab .ds-cta__title,
.smi-ab .ds-value-card__title,
.smi-ab .aut-title {
    font-family: Fraunces, Georgia, serif;
    font-weight: 700;
}
.smi-ab .ds-hero { background-color: #0f172a; }
.smi-ab .ds-hero:not(.ds-hero--with-image) { background: #0f172a; }
.smi-ab .ds-hero:not(.ds-hero--with-image) .ds-hero__title,
.smi-ab .ds-hero:not(.ds-hero--with-image) .ds-hero__subtitle { color: #f8fafc; }
.smi-ab .ds-stat-card__value { color: #ea580c; }
</style>
